/* eslint-disable import/order, @typescript-eslint/ban-ts-comment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars */
import { format, parseISO } from 'date-fns';
import { isValid } from 'date-fns/esm';
import enGb from 'date-fns/locale/en-GB';
import { FieldAttributes, useField, useFormikContext } from 'formik';
import moment from 'moment';
import React, { forwardRef } from 'react';
import {
  Button,
  Col,
  Form,
  FormControl,
  InputGroup,
  OverlayTrigger,
} from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import {
  FaChevronLeft,
  FaChevronRight,
  FaRegCalendarAlt,
} from 'react-icons/fa';

import { FieldLabel, FlexRow } from './../index';
import FieldTooltip from './field-tooltip';
import { useMask } from '@react-input/mask';
import './field-datepicker.css';
registerLocale('en-gb', enGb);

//@ts-ignore
//
//-------------------- Custom Input -------------------------------------------
//
const options = {
  mask: '__/__/____ __:__',
  replacement: { _: /\d/ },
};
const InputWithButton = forwardRef<any, any>(
  // @ts-ignore
  (
    {
      required,
      touched,
      submitCount,
      error,
      disabled,
      onClick,
      prefixLabel,
      prefixWidth,
      maskRef,
      ...rest
    },
    ref
  ) => (
    <InputGroup style={{ flex: 1 }}>
      {prefixLabel && (
        <InputGroup.Text
          style={{
            borderTopRightRadius: '0',
            borderBottomRightRadius: '0',
            width: prefixWidth,
          }}
        >
          {prefixLabel}
        </InputGroup.Text>
      )}
      <FormControl
        style={required && !disabled ? { backgroundColor: 'lightYellow' } : {}}
        {...rest}
        onClick={() => false}
        isInvalid={touched && !!error}
        disabled={disabled}
        ref={maskRef}
        // onFocus={() => false}
        // onChange={() => false }
      />
      <Button
        variant="primary"
        className="react-datepicker-ignore-onclickoutside btn-dark"
        style={{
          paddingTop: '0.1rem',
          paddingBottom: '0.1rem',
          paddingLeft: '0.2rem',
          paddingRight: '0.2rem',
          borderTopRightRadius: '0.3rem',
          borderBottomRightRadius: '0.3rem',
          backgroundColor: '#aeaeae',
          borderColor: '#ced4da',
        }}
        // @ts-ignore
        disabled={disabled}
        // @ts-ignore
        onClick={onClick}
      >
        <FaRegCalendarAlt
          size="0.9rem"
          style={{ marginBottom: '11px', verticalAlign: 'top' }}
        />
      </Button>
      {/*
 // @ts-ignore   */}
      {/* <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback> */}
      {(touched || submitCount > 0) && (
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      )}
    </InputGroup>
  )
);
//---------------------------------------------------------------------------
/*
 for DateTime it needs
      <FieldDatepicker
                  as={FormCol6}
                  label="Ημ/νία Ελέγχου"
                  name="filledDate"
                  disabled={!canEdit}
                  showTimeSelect
                  dateFormat="d/M/yyyy HH:mm"
                  timeFormat="HH:mm"
                  timeIntervals={15}
                />
*/

type Props = {
  readonly as?: React.ElementType<any>;
  readonly label: string;
  readonly placeholder?: string;
  readonly dateFormat?: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly maxDate?: Date;
  readonly minDate?: Date;
  readonly disableWeekend?: boolean;
  readonly colorWeekend?: boolean;
  readonly prefixLabel?: string;
  readonly prefixWidth?: string;
  readonly hasButtons?: boolean;
  readonly onChange?: any;
} & FieldAttributes<any>;

const FieldDatePicker = ({
  as = 'div',
  name,
  label,
  placeholder = '',
  dateFormat = 'dd/MM/yyyy',
  hasMask = false,
  disabled = false,
  required = false,
  maxDate,
  minDate,
  disableWeekend = false,
  colorWeekend = false,
  prefixLabel,
  prefixWidth = '7rem',
  hasButtons = false,
  onChange = undefined,
  ...rest
}: Props) => {
  //
  //-------------------- Body -----------------------------------------------
  //
  const [{ value }, { error, initialValue, touched }] = useField<Date>(name);
  const { setFieldValue, setFieldTouched, values, submitCount } =
    useFormikContext<any>();
  // const [actualValue, setActualValue] = useState<Date>(typeof value === 'string' ? parseISO(value) : value);

  // useEffect(() => {
  //   console.log('Value changed:', value, value !== actualValue);
  //   if (value !== actualValue) {
  //     setActualValue(typeof value === 'string' ? parseISO(value) : value);
  //   }
  // }, [value]); // value  only

  //
  //-------------------- Handlers -------------------------------------------
  //
  const setFieldValueAndTouched = async (
    _value: Date | null,
    touched: boolean
  ) => {
    if (isValid(_value)) {
      await setFieldValue(name, _value, true); //field,value,shouldValidate
    } else {
      await setFieldValue(name, null, true); //field,value,shouldValidate
    }
    await setFieldTouched(name, touched, false); //field,touched,shouldValidate
  };

  const handleChange = (date: Date) => {
    // setActualValue(date);
    setFieldValueAndTouched(date, true).then(() => {
      !!onChange && onChange({ ...values, [name]: date });
    });
  };

  const handleBlur = () => {
    setFieldValueAndTouched(value, true);
  };
  const changed = value !== initialValue;

  const isWeekday = (date) => {
    const day = new Date(date).getDay();
    return day !== 0 && day !== 6;
  };

  const handleDateColor = (date) => {
    return colorWeekend && (date.getDay() === 6 || date.getDay() === 0)
      ? 'text-error'
      : undefined;
  };

  const handlePeriodClick = (direction: 'next' | 'previous') => {
    // Μετάβαση σε Επόμενο ή Προηγούμενο Χρονικό Διάστημα
    let fromDate: Date = value;
    if (fromDate === null) {
      // Αρχικοποίηση στο σήμερα
      fromDate = moment().startOf('day').toDate();
    } else if (fromDate !== null) {
      // keep date only & disgard time
      fromDate = new Date(
        value.getFullYear(),
        value.getMonth(),
        value.getDate()
      );

      // Μετακινήσου 1 ημέρα
      const days2add = direction === 'next' ? 1 : -1;
      fromDate.setDate(fromDate.getDate() + days2add);
    }
    setFieldValue(name, fromDate, false);
    !!onChange && onChange({ ...values, [name]: fromDate });
  };

  const inputRef = useMask(options);
  // useWhyDidYouUpdate('[FieldDatePicker]', { name, value, error, initialValue, touched, actualValue });
  //
  //-------------------- Render -------------------------------------------
  //
  return (
    <Form.Group as={as} className="mb-2" controlId={name}>
      <FlexRow>
        <Col style={{ paddingLeft: '0px' }}>
          <FieldLabel text={label} changed={changed} required={required} />
          <OverlayTrigger
            delay={{ show: 1000, hide: 400 }}
            overlay={
              <FieldTooltip
                text={name}
                value={
                  typeof value === 'string'
                    ? format(parseISO(value), 'dd/MM/yyyy')
                    : value
                    ? format(value, 'dd/MM/yyyy')
                    : ''
                }
              />
            }
          >
            <InputGroup className="mb-1">
              <DatePicker
                autoComplete="off"
                preventOpenOnFocus
                placeholderText={placeholder}
                dateFormat={dateFormat}
                selected={typeof value === 'string' ? parseISO(value) : value}
                //@ts-ignore
                customInput={
                  //@ts-ignore
                  <InputWithButton
                    name={name}
                    maskRef={hasMask ? inputRef : undefined}
                    touched={touched}
                    error={error}
                    prefixLabel={prefixLabel}
                    prefixWidth={prefixWidth}
                    submitCount={submitCount}
                  />
                }
                onChange={handleChange}
                onBlur={handleBlur}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                disabled={disabled}
                required={required}
                minDate={minDate}
                maxDate={maxDate}
                filterDate={disableWeekend ? isWeekday : undefined}
                dayClassName={handleDateColor}
                // required={props.required}
                {...rest}
              />
            </InputGroup>
          </OverlayTrigger>
        </Col>
        {hasButtons && (
          <Col xs="auto" style={{ paddingRight: '0px', paddingTop: '0.8rem' }}>
            {/* priorPeriod */}
            <Button
              type="button"
              className="btn btn-secondary"
              style={{
                marginRight: '3px',
                backgroundColor: '#aeaeae',
                borderColor: '#aeaeae',
                height: '1.4em',
                width: '1.4em',
                paddingLeft: '4px',
                paddingRight: '4px',
              }}
              onClick={(e) => {
                handlePeriodClick('previous');
              }}
            >
              <FaChevronLeft size="0.8em" style={{ marginBottom: 1 }} />
            </Button>
            {/* nextPeriod */}
            <Button
              type="button"
              className="btn btn-secondary"
              style={{
                marginRight: '0px',
                backgroundColor: '#aeaeae',
                borderColor: '#aeaeae',
                height: '1.4rem',
                width: '1.4rem',
                paddingLeft: '4px',
                paddingRight: '4px',
              }}
              onClick={(e) => {
                handlePeriodClick('next');
              }}
            >
              <FaChevronRight size="0.8rem" style={{ marginBottom: 1 }} />
            </Button>
          </Col>
        )}
      </FlexRow>
    </Form.Group>
  );
};

export default FieldDatePicker;
